import { useAuth as useOidcAuth } from "react-oidc-context";
import { setAuthState, setDevAuthState } from "./bridge";

export const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === "true";

const DEV_USER_NAME = import.meta.env.VITE_DEV_AUTH_NAME ?? "Dev User";
const DEV_USER_EMAIL = import.meta.env.VITE_DEV_AUTH_EMAIL ?? "dev@local";
// Match this to `Auth:DevBypass:UserSub` on the API side so client-side
// "am I the owner?" checks agree with what the server thinks.
const DEV_USER_ID = import.meta.env.VITE_DEV_AUTH_USER_SUB ?? "dev-user";
// Matches the sentinel the dev-bypass auth handler on the API side accepts —
// the API doesn't actually validate the token in bypass mode, but sending
// SOMETHING in Authorization keeps the request shape identical to prod.
const DEV_TOKEN = "dev";

export interface Session {
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: Error;
  /** Stable user id — the JWT `sub` in OIDC mode, or `VITE_DEV_AUTH_USER_SUB` in bypass. */
  userId: string | null;
  name: string | null;
  email: string | null;
  accessToken: string | undefined;
  signin: () => Promise<unknown> | unknown;
  signout: () => Promise<unknown> | unknown;
}

/**
 * Picked at module load — same hook reference for the life of the SPA, so
 * React's rules-of-hooks invariant holds across every call site regardless of
 * which mode is active.
 */
export const useSession: () => Session = DEV_AUTH
  ? function useSessionDev() {
      // Keep the non-React auth bridge in sync so beforeLoad guards, the fetch
      // wrapper, and SignalR all behave the same as in OIDC mode. Pass the
      // sentinel token via the dedicated dev-bypass entry so
      // `authBridge.getAccessToken()` returns DEV_TOKEN — matters because
      // `apiFetch` and the SignalR factories both gate the Authorization
      // header on that value.
      setDevAuthState(DEV_TOKEN);
      return {
        isLoading: false,
        isAuthenticated: true,
        userId: DEV_USER_ID,
        name: DEV_USER_NAME,
        email: DEV_USER_EMAIL,
        accessToken: DEV_TOKEN,
        signin: () => {
          // No-op in dev — caller should never reach a signin path because
          // beforeLoad redirects authed users away from /login.
        },
        signout: () => {
          // Force a soft reset of the dev session by reloading. Real signout
          // doesn't make sense without an IdP, but giving the button SOME
          // behavior is useful when iterating UI.
          window.location.reload();
        },
      };
    }
  : function useSessionOidc() {
      const auth = useOidcAuth();
      setAuthState(
        auth.user ?? null,
        auth.isAuthenticated,
        auth.isLoading,
        () => auth.signinRedirect(),
        () => auth.signoutRedirect(),
      );
      return {
        isLoading: auth.isLoading,
        isAuthenticated: auth.isAuthenticated,
        error: auth.error,
        userId: auth.user?.profile.sub ?? null,
        name:
          auth.user?.profile.name ??
          (auth.user?.profile.preferred_username as string | undefined) ??
          null,
        email: (auth.user?.profile.email as string | undefined) ?? null,
        accessToken: auth.user?.access_token,
        signin: () => auth.signinRedirect(),
        signout: () => auth.signoutRedirect(),
      };
    };
